import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { useChangeAwareEffect } from 'use-change-aware-effect';

import {
  Button,
  Container,
  SectionSeparator,
  SectionTitle,
  SuccessMessage,
} from '../../common/form';

import useAnnouncementModalStore from '../../../announcement/state-hook';
import { propTypes, defaultProps } from '../prop-types';
import useAnnouncementStore from './state-hook';
import {
  createAnnouncement,
  updateAnnouncement,
} from '../../../../services/lwadmin/announcement/announcement-service';

import withNameFileUpload from '../../common/file-upload';
import withNameInput from '../../common/input';
import withNameTextarea from '../../common/textarea';
import withNameDatepicker from '../../common/datepicker';
import withNameSelect from '../../common/select';
import AnnouncementModal from '../../../announcement/announcement-modal';

const Name = withNameInput('announcement', 'name', useAnnouncementStore);
const HeroImage = withNameFileUpload('announcement', 'heroImage', useAnnouncementStore, 'image/gif, image/png, image/jpeg', 'Hero Image');
const Headline = withNameInput('announcement', 'headline', useAnnouncementStore);
const Copy = withNameTextarea('announcement', 'copy', useAnnouncementStore);
const ButtonCopy = withNameInput('announcement', 'buttonCopy', useAnnouncementStore, 'Button Copy');
const ButtonLink = withNameInput('announcement', 'buttonLink', useAnnouncementStore, 'Button Link');
const ExpiryDate = withNameDatepicker('announcement', 'expiryDate', useAnnouncementStore, 'Expiry Date');
const UserType = withNameSelect('announcement', 'userType', useAnnouncementStore, 'User Type');
const Companies = withNameSelect('announcement', 'companies', useAnnouncementStore, 'Companies', true);
const InvestorTypes = withNameSelect('announcement', 'investorTypes', useAnnouncementStore, 'Investor Types', true, { label: 'Select All', value: -1 });
const PagesRegex = withNameInput('announcement', 'pagesRegex', useAnnouncementStore, 'Pages Regex');
const UserEmails = withNameTextarea('announcement', 'userEmails', useAnnouncementStore, 'User Emails');

const SContainer = styled(Container)`
  padding-bottom: 40px;
`;

const Link = styled.a`
  font-weight: ${props => props.theme.fontWeightBold};
  outline: none !important;
`;

export default function Form(props) {
  const [saving, setSaving] = React.useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);

  const set = useAnnouncementStore(state => state.set);
  const announcement = useAnnouncementStore(state => state.announcement);
  const userType = useAnnouncementStore(state => state.announcement.userType);
  const UserGroup = withNameSelect('announcement', 'userGroup', useAnnouncementStore, 'User Group', false, null);

  React.useEffect(() => {
    if (userType !== 'logged_in') {
      set(state => {
        state.announcement.userGroup = null;
      });
    }
  });

  const userGroup = useAnnouncementStore(state => state.announcement.userGroup);

  const setModal = useAnnouncementModalStore(state => state.set);
  const announcementModal = useAnnouncementModalStore(
    state => state.currentAnnouncement,
  );

  const { id } = announcement;

  useChangeAwareEffect(({ isMount }) => {
    isMount && set(state => {
      state.announcement = (props || defaultProps).announcement;
      state.companiesOptions = props.companiesOptions;
      state.investorTypesOptions = props.investorTypesOptions;
    });
  }, {});

  const saveAnnouncement = e => {
    e && e.preventDefault();

    setSaving(true);

    const payload = {
      name: announcement.name,
      hero_image: announcement.heroImage,
      headline: announcement.headline,
      copy: announcement.copy,
      button_copy: announcement.buttonCopy,
      button_link: announcement.buttonLink,
      expiry_date: announcement.expiryDate,
      user_group: announcement.userGroup,
      user_type: announcement.userType,
      companies: announcement.companies,
      investor_types: announcement.investorTypes,
      pages_regex: announcement.pagesRegex,
      user_emails: announcement.userEmails,
    };

    const createOrUpdate = id
      ? updateAnnouncement(id, payload)
      : createAnnouncement(payload);

    createOrUpdate.then(resp => {
      setSaving(false);
      setShowSuccessMessage(true);

      const createdId = _.get(resp, 'data.id');
      if (createdId) {
        set(state => {
          state.announcement.id = createdId;
        });
      }

      set(state => {
        state.errors = {};
      });
    }).catch(error => {
      setSaving(false);
      setShowSuccessMessage(false);

      set(state => {
        state.errors = _.get(error, 'response.data', {});
      });

      setSaving(false);
    });
  };

  const preview = e => {
    e && e.preventDefault();

    setModal(state => {
      state.currentAnnouncement = announcement;
      state.show = true;
    });
  };

  return (
    <SContainer>
      {showSuccessMessage && (
        <SuccessMessage>
          Feature Announcement successfully saved
        </SuccessMessage>
      )}
      <Name />
      <HeroImage />
      <Headline />
      <Copy />
      <ButtonCopy />
      <ButtonLink />
      <ExpiryDate />
      <Link href="#" onClick={preview}>Preview Feature Announcement</Link>
      <SectionTitle>Announcement Targeting</SectionTitle>
      <SectionSeparator />
      <PagesRegex />
      <UserType />
      {userType === 'logged_in' && <UserGroup />}
      {userType === 'logged_in' && userGroup === 'users' && <InvestorTypes />}
      {userType === 'logged_in' && userGroup === 'contributors' && <Companies />}
      {userType === 'user_emails' && <UserEmails />}
      <Button className="btn btn-primary" onClick={saveAnnouncement} disabled={saving}>
        {saving ? 'Saving' : 'Save'}
      </Button>
      <AnnouncementModal announcement={announcementModal} preview />
    </SContainer>
  );
}

Form.propTypes = propTypes;
Form.defaultProps = defaultProps;
